import { ClerkProvider } from '@clerk/react-router'
import { rootAuthLoader } from '@clerk/react-router/ssr.server'
import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router'
import { PHProvider } from './provider'
import type { Route } from './+types/root'
import './app.css'

export async function loader(args: Route.LoaderArgs) {
  return rootAuthLoader(args)
}

export const links: Route.LinksFunction = () => [
  {
    rel: 'preload',
    href: '/fonts/Inter-roman.var.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'preload',
    href: '/fonts/Inter-italic.var.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  }
]

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="nl">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <PHProvider>
          {children}
          <ScrollRestoration />
          <Scripts />
        </PHProvider>
      </body>
    </html>
  )
}

export default function App({ loaderData }: Route.ComponentProps) {
  return (
    <ClerkProvider loaderData={loaderData}>
      <Outlet />
    </ClerkProvider>
  )
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Er is iets misgegaan'
  let details = 'Er is een onverwachte fout opgetreden. Probeer het later opnieuw.'
  let stack: string | undefined
  let statusCode: number | undefined

  if (isRouteErrorResponse(error)) {
    statusCode = error.status
    if (error.status === 404) {
      message = 'Pagina niet gevonden'
      details = 'De pagina die u zoekt bestaat niet of is verplaatst.'
    } else if (error.status === 403) {
      message = 'Geen toegang'
      details = 'U heeft geen toestemming om deze pagina te bekijken.'
    } else if (error.status === 500) {
      message = 'Serverfout'
      details = 'Er is een fout opgetreden op de server. We werken aan een oplossing.'
    } else {
      message = `Fout ${error.status}`
      details = error.statusText || details
    }
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message
    stack = error.stack
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Simple header bar */}
      <header className="fixed z-50 w-full border-b border-gray-300 bg-white shadow-md">
        <div className="mx-auto flex h-16 w-full max-w-5xl items-center justify-between px-4 lg:px-2">
          <a href="/" className="flex items-center gap-x-2">
            <div className="text-base font-bold uppercase">WeUniteBrands</div>
          </a>
        </div>
      </header>

      {/* Error content */}
      <main className="mx-auto w-full max-w-2xl flex-1 px-4 pt-24 pb-8">
        <div className="rounded-lg border border-gray-300 bg-white p-8 text-center shadow-md">
          {statusCode && <div className="mb-4 text-6xl font-bold text-gray-300">{statusCode}</div>}
          <h1 className="mb-4 text-2xl font-bold text-gray-900">{message}</h1>
          <p className="mb-6 text-gray-600">{details}</p>

          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <a
              href="/"
              className="inline-flex items-center justify-center rounded border border-[#2f6962] bg-[#2f6962] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#2f6962]/90">
              Terug naar home
            </a>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center justify-center rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-[#2f6962] transition-colors hover:bg-[#2f6962]/10">
              Ga terug
            </button>
          </div>

          {stack && (
            <details className="mt-8 text-left">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                Technische details (development mode)
              </summary>
              <pre className="mt-4 overflow-x-auto rounded bg-gray-100 p-4 text-xs text-gray-800">
                <code>{stack}</code>
              </pre>
            </details>
          )}
        </div>
      </main>

      {/* Simple footer */}
      <footer className="w-full border-t border-gray-300 bg-white px-6 py-6 text-center text-sm text-gray-600">
        <p>Copyright 2025 © WeUniteBrands</p>
      </footer>
    </div>
  )
}
